<template>
    <div id="cohorts-forum" class="forum" data-testid="forum-section">
        <div class="forum-toolbar">
            <div class="forum-toolbar__left">
                <button
                    type="button"
                    class="btn btn-sm btn-outline-secondary"
                    data-testid="forum-back-cohorts"
                    @click="$emit('close')"
                >
                    ← Turmas
                </button>
                <div class="forum-toolbar__title">
                    <span class="forum-toolbar__label">Fórum</span>
                    <strong>{{ cohortName || 'Turma' }}</strong>
                </div>
            </div>
            <button
                v-if="view === 'feed'"
                type="button"
                class="btn btn-primary btn-sm"
                data-testid="forum-new-post-open"
                @click="view = 'create'"
            >
                Novo post
            </button>
        </div>

        <!-- Detalhe -->
        <div v-if="view === 'detail' && selectedPost" class="forum-detail">
            <button type="button" class="btn btn-sm btn-link forum-back-link" @click="backToFeed">
                ← Voltar ao fórum
            </button>

            <article class="forum-card forum-card--post">
                <header class="forum-author">
                    <img
                        :src="authorPhoto(selectedPost)"
                        :alt="selectedPost.author_name || 'Autor'"
                        class="forum-avatar forum-avatar--lg"
                        @error="onAvatarError"
                    >
                    <div class="forum-author__meta">
                        <div class="forum-author__row">
                            <strong class="forum-author__name">{{ selectedPost.author_name || 'Autor' }}</strong>
                            <span
                                class="forum-role"
                                :class="selectedPost.author_type === 'mentor' ? 'forum-role--mentor' : 'forum-role--mentee'"
                            >
                                {{ roleLabel(selectedPost.author_type) }}
                            </span>
                        </div>
                        <time class="forum-author__date">{{ formatDate(selectedPost.created_at) }}</time>
                    </div>
                </header>
                <h5 class="forum-card__title" data-testid="forum-post-title">{{ selectedPost.title }}</h5>
                <div class="forum-card__body">{{ selectedPost.body }}</div>
            </article>

            <h6 class="forum-section-title">
                Respostas
                <span class="forum-badge">{{ (selectedPost.replies && selectedPost.replies.length) || 0 }}</span>
            </h6>

            <div
                v-for="reply in (selectedPost.replies || [])"
                :key="'reply-' + reply.id"
                class="forum-card forum-card--reply"
                data-testid="forum-reply-item"
            >
                <header class="forum-author">
                    <img
                        :src="authorPhoto(reply)"
                        :alt="reply.author_name || 'Autor'"
                        class="forum-avatar"
                        @error="onAvatarError"
                    >
                    <div class="forum-author__meta">
                        <div class="forum-author__row">
                            <strong class="forum-author__name">{{ reply.author_name || 'Autor' }}</strong>
                            <span
                                class="forum-role"
                                :class="reply.author_type === 'mentor' ? 'forum-role--mentor' : 'forum-role--mentee'"
                            >
                                {{ roleLabel(reply.author_type) }}
                            </span>
                        </div>
                        <time class="forum-author__date">{{ formatDate(reply.created_at) }}</time>
                    </div>
                </header>
                <div class="forum-card__body">{{ reply.body }}</div>
            </div>

            <div v-if="!(selectedPost.replies && selectedPost.replies.length)" class="forum-empty forum-empty--soft">
                Nenhuma resposta ainda.
            </div>

            <form class="forum-compose" data-testid="forum-reply-form" @submit.prevent="submitReply">
                <label for="forum-reply-body" class="forum-compose__label">Sua resposta</label>
                <textarea
                    id="forum-reply-body"
                    v-model="replyBody"
                    class="form-control"
                    rows="3"
                    required
                    placeholder="Escreva um comentário…"
                    data-testid="forum-reply-body"
                ></textarea>
                <button
                    type="submit"
                    class="btn btn-primary btn-sm mt-2"
                    :disabled="savingReply"
                    data-testid="forum-reply-submit"
                >
                    {{ savingReply ? 'Enviando…' : 'Responder' }}
                </button>
            </form>
        </div>

        <!-- Criar -->
        <div v-else-if="view === 'create'" class="forum-create">
            <button type="button" class="btn btn-sm btn-link forum-back-link" @click="backToFeed">
                ← Voltar ao fórum
            </button>

            <form class="forum-compose" data-testid="forum-new-post" @submit.prevent="submitPost">
                <h5 class="mb-3">Novo post</h5>
                <div class="form-group">
                    <label for="forum-post-title-input">Título</label>
                    <input
                        id="forum-post-title-input"
                        v-model="newPost.title"
                        type="text"
                        class="form-control"
                        required
                        maxlength="255"
                        data-testid="forum-post-title"
                    >
                </div>
                <div class="form-group">
                    <label for="forum-post-body-input">Conteúdo</label>
                    <textarea
                        id="forum-post-body-input"
                        v-model="newPost.body"
                        class="form-control"
                        rows="5"
                        required
                        data-testid="forum-post-body"
                    ></textarea>
                </div>
                <button
                    type="submit"
                    class="btn btn-primary"
                    :disabled="savingPost"
                    data-testid="forum-new-post-submit"
                >
                    {{ savingPost ? 'Publicando…' : 'Publicar' }}
                </button>
            </form>
        </div>

        <!-- Feed -->
        <div v-else class="forum-feed-wrap">
            <div v-if="loading" class="text-center text-muted py-4">Carregando…</div>

            <div v-else-if="error" class="alert alert-warning" data-testid="forum-error">
                {{ error }}
            </div>

            <div v-else-if="!posts.length" class="forum-empty">
                Nenhum post ainda. Seja o primeiro a publicar no fórum.
            </div>

            <div v-else class="forum-feed" data-testid="forum-feed">
                <button
                    v-for="post in posts"
                    :key="'post-' + post.id"
                    type="button"
                    class="forum-card forum-card--feed"
                    data-testid="forum-feed-item"
                    @click="openPost(post.id)"
                >
                    <img
                        :src="authorPhoto(post)"
                        :alt="post.author_name || 'Autor'"
                        class="forum-avatar forum-avatar--lg"
                        @error="onAvatarError"
                    >
                    <div class="forum-card__content">
                        <div class="forum-card__top">
                            <h6 class="forum-card__title mb-0" data-testid="forum-post-title">{{ post.title }}</h6>
                            <time class="forum-author__date">{{ formatDate(post.created_at) }}</time>
                        </div>
                        <div class="forum-author__row forum-author__row--feed">
                            <span class="forum-author__name">{{ post.author_name || 'Autor' }}</span>
                            <span
                                class="forum-role"
                                :class="post.author_type === 'mentor' ? 'forum-role--mentor' : 'forum-role--mentee'"
                            >
                                {{ roleLabel(post.author_type) }}
                            </span>
                            <span class="forum-replies">
                                {{ post.replies_count || 0 }}
                                {{ Number(post.replies_count) === 1 ? 'resposta' : 'respostas' }}
                            </span>
                        </div>
                    </div>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import http from "@/http";
import env from "@/env";

const AVATAR_PLACEHOLDER =
    'data:image/svg+xml,' + encodeURIComponent(
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="none">' +
        '<rect width="64" height="64" rx="32" fill="#E8EEF5"/>' +
        '<circle cx="32" cy="24" r="12" fill="#9DB0C7"/>' +
        '<path d="M12 54c2.5-12 14-18 20-18s17.5 6 20 18" fill="#9DB0C7"/>' +
        '</svg>'
    );

export default {
    name: 'CohortsForum',

    props: {
        cohortId: {
            type: [Number, String],
            required: true,
        },
        cohortName: {
            type: String,
            default: '',
        },
    },

    data() {
        return {
            view: 'feed',
            posts: [],
            selectedPost: null,
            loading: false,
            error: '',
            savingPost: false,
            savingReply: false,
            replyBody: '',
            newPost: {
                title: '',
                body: '',
            },
        };
    },

    watch: {
        cohortId: {
            immediate: true,
            handler(id) {
                this.view = 'feed';
                this.selectedPost = null;
                if (id) this.fetchPosts();
            },
        },
    },

    methods: {
        formatDate(value) {
            if (!value) return '—';
            const date = new Date(String(value).replace(' ', 'T'));
            if (Number.isNaN(date.getTime())) return value;
            return date.toLocaleString('pt-BR');
        },

        roleLabel(type) {
            return type === 'mentor' ? 'Mentor' : 'Mentorado';
        },

        authorPhoto(item) {
            if (!item) return AVATAR_PLACEHOLDER;
            if (item.author_photo_url) return item.author_photo_url;
            if (!item.author_type || !item.author_id) return AVATAR_PLACEHOLDER;
            const path = item.author_type === 'mentor' ? 'mentors' : 'mentees';
            return `${env.url}${path}/image/${item.author_id}`;
        },

        onAvatarError(event) {
            if (event && event.target) {
                event.target.src = AVATAR_PLACEHOLDER;
            }
        },

        backToFeed() {
            this.view = 'feed';
            this.selectedPost = null;
            this.replyBody = '';
            this.newPost = {title: '', body: ''};
            this.fetchPosts();
        },

        fetchPosts() {
            if (!this.cohortId) {
                this.error = 'Turma não informada para o fórum.';
                this.posts = [];
                return;
            }

            this.loading = true;
            this.error = '';
            http.get(`cohorts/${this.cohortId}/forum/posts`)
                .then(response => {
                    this.posts = Array.isArray(response.data) ? response.data : [];
                })
                .catch(e => {
                    console.error(e);
                    const msg = e.response?.data?.message || 'Não foi possível carregar o fórum.';
                    this.error = msg;
                    this.posts = [];
                    if (e.response?.status !== 403) {
                        this.$notifyError(e.response?.data || e);
                    }
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        openPost(postId) {
            this.loading = true;
            http.get(`forum/posts/${postId}`)
                .then(response => {
                    this.selectedPost = response.data;
                    this.view = 'detail';
                    this.replyBody = '';
                })
                .catch(e => {
                    console.error(e);
                    this.$notifyError(e.response?.data || e);
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        submitPost() {
            const title = (this.newPost.title || '').trim();
            const body = (this.newPost.body || '').trim();
            if (!title || !body) {
                this.$notifyError('Informe título e conteúdo.');
                return;
            }

            this.savingPost = true;
            http.post(`cohorts/${this.cohortId}/forum/posts`, {
                title: title,
                body: body,
            })
                .then(() => {
                    this.$notifySuccess('Post publicado!');
                    this.newPost = {title: '', body: ''};
                    this.backToFeed();
                })
                .catch(e => {
                    console.error(e);
                    this.$notifyError((e.response && e.response.data) || e);
                })
                .finally(() => {
                    this.savingPost = false;
                });
        },

        submitReply() {
            const body = (this.replyBody || '').trim();
            if (!body || !this.selectedPost || !this.selectedPost.id) {
                this.$notifyError('Escreva uma resposta.');
                return;
            }

            this.savingReply = true;
            http.post(`forum/posts/${this.selectedPost.id}/replies`, {
                body: body,
            })
                .then(() => {
                    this.$notifySuccess('Resposta enviada!');
                    this.replyBody = '';
                    this.openPost(this.selectedPost.id);
                })
                .catch(e => {
                    console.error(e);
                    this.$notifyError((e.response && e.response.data) || e);
                })
                .finally(() => {
                    this.savingReply = false;
                });
        },
    },
};
</script>

<style scoped>
.forum {
    --forum-surface: #f4f7fb;
    --forum-border: #e2e8f0;
    --forum-text: #1e293b;
    --forum-muted: #64748b;
    --forum-accent: #0f766e;
    --forum-mentor: #0f766e;
    --forum-mentee: #0369a1;
}

.forum-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1.25rem;
    flex-wrap: wrap;
}

.forum-toolbar__left {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    min-width: 0;
}

.forum-toolbar__title {
    display: flex;
    flex-direction: column;
    line-height: 1.2;
    color: var(--forum-text);
}

.forum-toolbar__label {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--forum-muted);
}

.forum-back-link {
    padding-left: 0;
    margin-bottom: 0.75rem;
}

.forum-feed {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.forum-card {
    background: #fff;
    border: 1px solid var(--forum-border);
    border-radius: 14px;
    padding: 1rem 1.1rem;
    text-align: left;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.forum-card--feed {
    display: flex;
    gap: 0.9rem;
    width: 100%;
    cursor: pointer;
    transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}

.forum-card--feed:hover,
.forum-card--feed:focus {
    border-color: #cbd5e1;
    box-shadow: 0 6px 18px rgba(15, 23, 42, 0.08);
    transform: translateY(-1px);
    outline: none;
}

.forum-card__content {
    flex: 1;
    min-width: 0;
}

.forum-card__top {
    display: flex;
    justify-content: space-between;
    gap: 0.75rem;
    align-items: flex-start;
    margin-bottom: 0.35rem;
}

.forum-card__title {
    color: var(--forum-text);
    font-weight: 600;
    word-break: break-word;
}

.forum-card__body {
    white-space: pre-wrap;
    color: var(--forum-text);
    line-height: 1.55;
    margin-top: 0.65rem;
}

.forum-card--post {
    margin-bottom: 1.25rem;
    background: linear-gradient(180deg, #fff 0%, var(--forum-surface) 100%);
}

.forum-card--reply {
    margin-bottom: 0.65rem;
}

.forum-author {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.forum-author__meta {
    min-width: 0;
}

.forum-author__row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.4rem;
}

.forum-author__row--feed {
    margin-top: 0.15rem;
}

.forum-author__name {
    color: var(--forum-text);
    font-size: 0.92rem;
}

.forum-author__date {
    display: block;
    color: var(--forum-muted);
    font-size: 0.78rem;
    white-space: nowrap;
}

.forum-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
    background: #e8eef5;
    border: 2px solid #fff;
    box-shadow: 0 0 0 1px var(--forum-border);
}

.forum-avatar--lg {
    width: 48px;
    height: 48px;
}

.forum-role {
    display: inline-flex;
    align-items: center;
    font-size: 0.68rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    padding: 0.12rem 0.45rem;
    border-radius: 999px;
    line-height: 1.2;
}

.forum-role--mentor {
    color: var(--forum-mentor);
    background: rgba(15, 118, 110, 0.12);
}

.forum-role--mentee {
    color: var(--forum-mentee);
    background: rgba(3, 105, 161, 0.12);
}

.forum-replies {
    color: var(--forum-muted);
    font-size: 0.8rem;
}

.forum-replies::before {
    content: '·';
    margin-right: 0.35rem;
}

.forum-section-title {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0 0 0.85rem;
    color: var(--forum-text);
}

.forum-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1.4rem;
    height: 1.4rem;
    padding: 0 0.4rem;
    border-radius: 999px;
    background: var(--forum-surface);
    border: 1px solid var(--forum-border);
    font-size: 0.75rem;
    color: var(--forum-muted);
}

.forum-compose {
    margin-top: 1rem;
    padding: 1rem 1.1rem;
    background: #fff;
    border: 1px solid var(--forum-border);
    border-radius: 14px;
}

.forum-compose__label {
    font-weight: 600;
    color: var(--forum-text);
    margin-bottom: 0.4rem;
    display: block;
}

.forum-empty {
    text-align: center;
    padding: 2rem 1rem;
    color: var(--forum-muted);
    background: var(--forum-surface);
    border: 1px dashed var(--forum-border);
    border-radius: 14px;
}

.forum-empty--soft {
    margin-bottom: 1rem;
    padding: 1rem;
}
</style>
